import React, { useState, useEffect, useCallback, useRef } from "react";

interface SlideInfo {
  index: number;
  filename: string;
}

interface TrayData {
  letter: string;
  summary: string;
  images: SlideInfo[];
}

interface Props {
  letter: string;
  onBack: () => void;
}

export function SlideshowPage({ letter, onBack }: Props) {
  const [trayData, setTrayData] = useState<TrayData | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(6);
  const [loading, setLoading] = useState(true);
  const [showIndex, setShowIndex] = useState(false);
  const [hasIndex, setHasIndex] = useState(false);
  const wasPlayingRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    fetch(`/api/slides/${letter}`)
      .then((r) => r.json())
      .then((data) => {
        setTrayData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Check if index photo exists
    fetch(`/api/slides/${letter}/index-photo`, { method: "HEAD" })
      .then((r) => setHasIndex(r.ok))
      .catch(() => setHasIndex(false));
  }, [letter]);

  const openIndex = useCallback(() => {
    wasPlayingRef.current = playing;
    setPlaying(false);
    setShowIndex(true);
  }, [playing]);

  const closeIndex = useCallback(() => {
    setShowIndex(false);
    if (wasPlayingRef.current) {
      setPlaying(true);
    }
  }, []);

  const totalSlides = trayData?.images.length || 0;

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= totalSlides - 1) {
        setPlaying(false);
        return prev;
      }
      return prev + 1;
    });
  }, [totalSlides]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Auto-play timer
  useEffect(() => {
    if (playing && totalSlides > 0) {
      timerRef.current = window.setInterval(goNext, speed * 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [playing, speed, goNext, totalSlides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case " ":
          e.preventDefault();
          goNext();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goPrev();
          break;
        case "Escape":
          e.preventDefault();
          onBack();
          break;
        case "p":
          e.preventDefault();
          setPlaying((prev) => !prev);
          break;
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev, onBack]);

  if (loading || !trayData) {
    return <div className="loading">Dia's laden...</div>;
  }

  return (
    <div className="slideshow-page">
      {/* Main image area */}
      <div className="slide-container">
        <div className="slide-vignette">
          <img
            src={`/api/slides/${letter}/${currentIndex}`}
            alt={`Dia ${currentIndex + 1}`}
            className="slide-image"
            key={currentIndex}
          />
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        className="nav-arrow nav-left"
        onClick={goPrev}
        disabled={currentIndex === 0}
      >
        &#10094;
      </button>
      <button
        className="nav-arrow nav-right"
        onClick={goNext}
        disabled={currentIndex >= totalSlides - 1}
      >
        &#10095;
      </button>

      {/* Top bar */}
      <div className="slideshow-topbar">
        <button className="btn btn-back" onClick={onBack}>
          &#8592; Overzicht
        </button>
        <div className="slide-info">
          Slee {letter} &mdash; Dia {currentIndex + 1} van {totalSlides}
        </div>
        <div className="slide-actions">
          <a
            href={`/api/slides/${letter}/${currentIndex}/original`}
            className="btn btn-download-small"
            title="Deze dia downloaden"
          >
            &#11015; Dia
          </a>
          <a
            href={`/api/download/${letter}`}
            className="btn btn-download-small"
            title="Hele slee downloaden"
          >
            &#11015; Slee {letter}
          </a>
        </div>
      </div>

      {/* Bottom controls */}
      <div className="slideshow-controls">
        <button
          className="btn-control"
          onClick={() => setPlaying((prev) => !prev)}
        >
          {playing ? "⏸" : "▶"}
        </button>
        {hasIndex && (
          <button
            className="btn-control"
            onClick={openIndex}
            title="Index bekijken"
          >
            &#128196;
          </button>
        )}
        <label className="speed-control">
          <span>Snelheid:</span>
          <input
            type="range"
            min="2"
            max="15"
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
          />
          <span>{speed}s</span>
        </label>
      </div>

      {/* Index photo modal */}
      {showIndex && (
        <div className="modal-overlay" onClick={closeIndex}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeIndex}>
              &times;
            </button>
            <h2>Index {letter}</h2>
            <img
              src={`/api/slides/${letter}/index-photo`}
              alt={`Index ${letter}`}
              className="index-photo"
            />
          </div>
        </div>
      )}
    </div>
  );
}
